import React, { forwardRef, useEffect, useState } from "react";
import { Form, Input, Button, Stepper, NavBar } from "antd-mobile";
import { PlusOutlined } from "@ant-design/icons";
import RemoteListPicker from "./RemoteListPicker";
import { requiredField } from "../common/common";
import MySignaturePad from "./MySignaturePad";
import { SignaturePage } from "./SignaturePage";
import dayjs from "dayjs";
import { api } from "../common/api";
import { useNavigate } from "react-router";

const EmployeePicker = forwardRef((props, ref) => (
  <RemoteListPicker
    title={"Seleziona dipendente"}
    fetchList={(params) => api.employee.list(params)}
    fetchItem={(id) => api.employee.get(id)}
    resource={"employee"}
    labelFn={(emp) => emp.fullName}
    placeholder={"Seleziona Dipendente"}
    ref={ref}
    {...props}
  />
));

const DpiProductPicker = forwardRef((props, ref) => (
  <RemoteListPicker
    title={"Seleziona prodotto"}
    placeholder={"Seleziona prodotto"}
    fetchList={(params) => api.dpiProduct.list(params)}
    fetchItem={(id) => api.dpiProduct.get(id)}
    resource={"dpiProduct"}
    labelFn={(x) => x.description}
    valueFn={(x) => x}
    ref={ref}
    {...props}
  />
));

const InventoryUnitPicker = forwardRef(({ isAssigned, assignedTo, dpiProduct, ...props }, ref) => (
  <RemoteListPicker
    title={"Seleziona unità di misura"}
    fetchList={(params) =>
      api.dpiInventoryUnit.list({ ...params, isAssigned, assignedTo, dpiProduct })
    }
    fetchItem={(id) => api.dpiInventoryUnit.get(id)}
    placeholder={"Seleziona unità di misura"}
    resource={"dpiInventoryUnit"}
    labelFn={(x) => x.serialNumber}
    valueFn={(x) => x}
    ref={ref}
    {...props}
  />
));

export default function ({ type }) {
  if (!type || !["delivery", "return"].includes(type)) throw new Error("type is required");
  const reasonId = type === "delivery" ? "CNS" : "SCART";
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const signatureWatch = Form.useWatch("signature", form);
  const employeeWatch = Form.useWatch("employee", form);
  const navigate = useNavigate();
  const onFinish = (values) => {
    const data = {
      employee: values.employee.value,
      date: dayjs().startOf("day").format("YYYY-MM-DD"),
      reason: reasonId,
      signature: values.signature,
      lines: values.lines.map((line) => {
        return {
          amount: line.quantity,
          productId: line.product.value.id,
          inventoryUnits: line.inventoryUnit?.value ? [line.inventoryUnit.value.id] : [],
        };
      }),
    };
    setLoading(true);
    api.dpiTransaction
      .create(data)
      .then(() => navigate(`/mobile/success?return=${window.location.pathname}`, { replace: true }))
      .finally(() => setLoading(false));
  };
  return (
    <div style={{ height: "100vh", overflowY: "auto", backgroundColor: "#f5f5f5" }}>
      <div>
        <NavBar
          onBack={() => navigate("..", { replace: true })}
          style={{ background: "#fff" }}
          right={
            !!signatureWatch && (
              <Button onClick={() => form.setFieldValue("signature", null)}>Sblocca</Button>
            )
          }
        >
          {type === "delivery" ? "Consegna DPI" : "Restituzione DPI"}
        </NavBar>
        <Form
          onFinish={onFinish}
          disabled={!!signatureWatch}
          form={form}
          initialValues={{
            lines: [{}],
          }}
          layout={"horizontal"}
          footer={
            <Button block color={"primary"} type={"submit"} loading={loading}>
              Salva
            </Button>
          }
          mode="card"
        >
          <Form.Item
            name={"employee"}
            label="Dipendente"
            rules={[requiredField]}
            onClick={(e, ref) => {
              console.log(ref);
              ref.current?.open();
            }}
          >
            <EmployeePicker />
          </Form.Item>
          <Form.Array
            name="lines"
            onAdd={(operation) => !signatureWatch && operation.add()}
            renderAdd={() => (
              <span>
                <PlusOutlined /> Aggiungi
              </span>
            )}
            renderHeader={({ index }, { remove }) => (
              <>
                <span>Riga #{index + 1}</span>
                <a onClick={() => remove(index)} style={{ float: "right" }}>
                  Rimuovi
                </a>
              </>
            )}
          >
            {(fields) =>
              fields.map(({ index }) => (
                <>
                  <Form.Item
                    name={[index, "product"]}
                    label="Prodotto"
                    rules={[requiredField]}
                    onClick={(e, ref) => ref.current?.open()}
                  >
                    <DpiProductPicker
                      onChange={() => {
                        form.setFieldValue(["lines", index, "inventoryUnit"], null);
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={[index, "quantity"]}
                    label="Quantità"
                    rules={[requiredField]}
                    initialValue={1}
                  >
                    <Stepper digits={0} min={1} />
                  </Form.Item>
                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => {
                      const item = getFieldValue(["lines", index, "product"])?.value;
                      const employee = getFieldValue("employee")?.value;
                      return (
                        item?.dpi.trackingType === "SERIAL" && (
                          <Form.Item
                            name={[index, "inventoryUnit"]}
                            rules={[requiredField]}
                            disabled={!!signatureWatch || (type === "return" && !employeeWatch)}
                            label={"Matricola"}
                            onClick={(e, ref) => ref.current?.open()}
                          >
                            <InventoryUnitPicker
                              isAssigned={type === "return"}
                              assignedTo={type === "delivery" ? null : employee}
                              dpiProduct={item.id}
                            />
                          </Form.Item>
                        )
                      );
                    }}
                  </Form.Item>
                </>
              ))
            }
          </Form.Array>
          <Form.Header>Firma</Form.Header>
          {type === "delivery" && (
            <Form.Item
              name={"signature"}
              rules={[requiredField]}
              label={"Firma del lavoratore"}
              onClick={(e, ref) => ref.current?.open()}
            >
              <SignaturePage />
            </Form.Item>
          )}
        </Form>
      </div>
    </div>
  );
}
